<template>
  <div class="i-professional-bio">
    <div class="head">
      <iBaseHeader
        :highlight-text="headerText"
        :text="reportStore.hasCurrentJob ? jobTitle : ''"
        :font-size-highlighted-text="headerSize"
        font-size-text="24px"
        font-weight-text="400"
        align="start"
      />
      <iDownloadPdf
        class="save"
        :set-row="true"
      />
    </div>
    <div
      v-if="statsData.length >= 1"
      class="stats"
    >
      <div
        v-for="(stat, index) in statsData.filter((e) => e?.length)"
        :key="stat"
        class="stat"
      >
        <div
          v-if="isTabletAndUp && index !== 0"
          class="circle"
        ></div>
        <iBaseTruncatedText
          class="label"
          :text="stat"
          :max-length="isTabletAndUp ? 39 : 100"
        />
      </div>
    </div>
    <div class="summary">
      {{ bioSummary }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue"
import { useReportStore } from "@/stores/report"
import { useUserStore } from "@/stores/user"

import { breakpointMd, breakpointSm } from "@/styles/variables"

import useIsScreenSize from "@/composables/useIsScreenSize"

import iBaseHeader from "@/components/base/BaseHeader.vue"
import iBaseTruncatedText from "@/components/base/BaseTruncatedText.vue"
import iDownloadPdf from "@/components/global/DownloadPdf.vue"

// STORES AND COMPOSABLES
const reportStore = useReportStore()
const userStore = useUserStore()
const isNotMobile = useIsScreenSize(breakpointSm)
const isTabletAndUp = useIsScreenSize(breakpointMd)

// COMPUTED
const headerSize = computed(() => (isNotMobile.value ? "36px" : "32px"))
const headerText = computed(() =>
  userStore.getFullName ? userStore.getFullName : reportStore.hasCurrentJob ? "Current Career" : "Hello"
)
const jobTitle = computed(() => reportStore.reportData?.user?.currentJob)
const bioSummary = computed(() => reportStore.getBio ?? "Your professional bio is coming Soon...")
const statsData = computed(
  () =>
    [reportStore.getEducationLevel, reportStore.getSchoolMajor, reportStore.getLocation].filter(
      (e) => e !== null
    ) as string[]
)
</script>

<style scoped lang="scss">
.i-professional-bio {
  @include flex(column, start, stretch, $gap: 25px);

  .head {
    @include flex(row, space-between, end, $gap: 10px);

    .save {
      @include border-radius-sm;

      border: 1px solid $zumthor100;
      width: fit-content;
      padding: 4px 12px;

      &:hover {
        background-color: $azure-radiance50;
        border-color: $azure-radiance200;
      }
    }
  }

  .stats {
    @include flex(column, start, stretch, $gap: 10px);
    @include text-sm;

    @media screen and (min-width: $breakpoint-sm) {
      gap: 5px;

      @include text-md;
    }

    @media screen and (min-width: $breakpoint-md) {
      @include flex(row, start, center, wrap, $gap: 5px 15px);
    }

    .stat {
      @include flex(row, start, baseline, $gap: 15px);

      @media screen and (min-width: $breakpoint-md) {
        align-items: center;
      }

      .label {
        color: $pickle-bluewood900;
      }

      .circle {
        flex: 0 0 6px;
        width: 6px;
        height: 6px;

        @include border-radius-rounded;

        background-color: #676474;
      }
    }
  }

  .summary {
    border-left: 5px solid $azure-radiance500;
    color: $pickle-bluewood900;
    padding-left: 25px;

    @include text-md;

    font-weight: 400;

    @media screen and (min-width: $breakpoint-sm) {
      padding-left: 30px;
      margin: 10px auto 0;
    }
  }
}
</style>
